import { Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router';
import { Search, WifiOff } from 'lucide-react';
import { usePermissions } from '@/lib/auth';
import { filterNav, STAFF_NAV } from '@/lib/nav/staff';
import { staffBreadcrumbs } from '@/lib/nav/breadcrumbs';
import { useHotkey, useHotkeySequence, useOnline, useStoredState, modKeyLabel } from '@/lib/hooks';
import { useI18n } from '@/lib/i18n';
import { Breadcrumbs } from '@/components/ui/misc';
import { Skeleton } from '@/components/ui/Skeleton';
import { Kbd } from '@/components/ui/misc';
import { CommandPalette } from './CommandPalette';
import { LibrarySwitcher } from './LibrarySwitcher';
import { LogoMark } from './Logo';
import { MobileTabBar } from './MobileNav';
import { NotificationsBell } from './NotificationsBell';
import { ShortcutSheet } from './ShortcutSheet';
import { Sidebar } from './Sidebar';
import { ThemeToggle } from './ThemeToggle';
import { UserMenu } from './UserMenu';

/** Shown while a lazily loaded feature route downloads. */
export function RouteFallback() {
  return (
    <div className="space-y-6" aria-busy="true" aria-live="polite">
      <span className="sr-only">Loading</span>
      <div className="space-y-2">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-4 w-96 max-w-full" />
      </div>
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {Array.from({ length: 4 }, (_, i) => (
          <Skeleton key={i} className="h-32 rounded-card" />
        ))}
      </div>
      <Skeleton className="h-72 rounded-card" />
    </div>
  );
}

/**
 * The staff console frame: navy sidebar (permission-filtered), glass top bar with search/palette,
 * library switcher, notifications, theme and user menu; bottom tab bar on phones.
 */
export function StaffShell() {
  const perms = usePermissions();
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useI18n();
  const online = useOnline();
  const groups = useMemo(() => filterNav(STAFF_NAV, perms), [perms]);
  const [collapsed, setCollapsed] = useStoredState<boolean>('lumina.sidebar.collapsed', false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const crumbs = useMemo(() => staffBreadcrumbs(location.pathname), [location.pathname]);

  const togglePalette = useCallback(() => setPaletteOpen((v) => !v), []);
  useHotkey('mod+k', togglePalette);
  useHotkey('mod+b', () => setCollapsed((c) => !c));
  useHotkey('?', () => setShortcutsOpen(true));
  useHotkeySequence('g', 'd', () => navigate('/staff'));
  useHotkeySequence('g', 'c', () => navigate('/staff/circulation'));
  useHotkeySequence('g', 'k', () => navigate('/staff/catalog'));
  useHotkeySequence('g', 'p', () => navigate('/staff/patrons'));
  useHotkeySequence('g', 'f', () => navigate('/staff/fines'));

  // Move focus to the main landmark after navigation so keyboard and screen-reader users start at the top of the new page.
  useEffect(() => {
    const main = document.getElementById('main');
    // Start at the top of the new page, unless the page already placed focus inside itself (e.g. the scan field).
    if (main && !main.contains(document.activeElement)) {
      main.focus({ preventScroll: true });
    }
    window.scrollTo({ top: 0 });
  }, [location.pathname]);

  return (
    <div className="flex min-h-dvh bg-canvas">
      <a href="#main" className="skip-link">
        {t('shell.skipToContent')}
      </a>
      <Sidebar groups={groups} collapsed={collapsed} onToggleCollapsed={() => setCollapsed((c) => !c)} />
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="no-print glass sticky top-0 z-30 flex h-16 shrink-0 items-center gap-3 border-b border-line px-4 lg:px-8">
          <LogoMark size={30} className="lg:hidden" />
          <button
            type="button"
            onClick={() => setPaletteOpen(true)}
            aria-label={t('shell.commandPalette')}
            aria-haspopup="dialog"
            className="group flex h-10 min-w-0 flex-1 items-center gap-2.5 rounded-xl border border-line bg-surface/80 px-3.5 text-left text-sm text-ink-subtle shadow-card transition hover:border-sapphire-300 hover:text-ink-muted sm:max-w-md"
          >
            <Search className="h-4 w-4 shrink-0" aria-hidden="true" />
            <span className="truncate">{t('shell.searchEverything')}</span>
            <span className="ml-auto hidden items-center gap-1 sm:flex">
              <Kbd>{modKeyLabel()}</Kbd>
              <Kbd>K</Kbd>
            </span>
          </button>
          <div className="ml-auto flex items-center gap-1.5">
            <div className="hidden md:block">
              <LibrarySwitcher />
            </div>
            <NotificationsBell />
            <ThemeToggle />
            <UserMenu onShortcuts={() => setShortcutsOpen(true)} />
          </div>
        </header>

        {!online && (
          <div role="status" className="flex items-center justify-center gap-2 bg-warning-50 px-4 py-2 text-sm font-medium text-warning-700 dark:bg-warning-500/15 dark:text-warning-500">
            <WifiOff className="h-4 w-4" aria-hidden="true" />
            {t('shell.offline')}
          </div>
        )}

        <main id="main" tabIndex={-1} className="mx-auto w-full max-w-page flex-1 px-4 pb-28 pt-6 outline-none lg:px-8 lg:pb-12">
          {crumbs.length > 0 && <Breadcrumbs items={crumbs} className="mb-3 -ml-1 hidden sm:block" />}
          <Suspense fallback={<RouteFallback />}>
            <Outlet />
          </Suspense>
        </main>
      </div>

      <MobileTabBar groups={groups} />
      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
      <ShortcutSheet open={shortcutsOpen} onClose={() => setShortcutsOpen(false)} />
    </div>
  );
}
